import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChefHat, 
  Shirt, 
  DoorOpen, 
  PanelTop,
  CheckCircle, 
  XCircle, 
  ArrowRight,
  Flame,
  Droplets,
  Sun,
  Wind,
  Sparkles,
  Shield,
  Clock,
  Award
} from 'lucide-react';

const ApplicationBenefits = () => {
  const [activeApp, setActiveApp] = useState('kitchen');

  const applications = {
    kitchen: {
      title: 'Modular Kitchens',
      icon: ChefHat,
      tagline: 'Your kitchen will look new even after 15 years of daily cooking',
      heroImage: '/crilam-kitchen-hero.jpg',
      problems: [
        {
          problem: 'Laminate peeling near gas stove',
          solution: 'Heat-resistant bond survives 150°C',
          icon: Flame
        },
        {
          problem: 'Edge lifting in humid sink areas',
          solution: '100% waterproof, no delamination',
          icon: Droplets
        },
        {
          problem: 'Yellowing from cooking oils',
          solution: 'Chemical-resistant surface stays pristine',
          icon: Sparkles
        },
        {
          problem: 'Shutters warping over time',
          solution: 'Dimensionally stable bond prevents warping',
          icon: Shield
        }
      ],
      stats: [
        { value: '150°C', label: 'Heat Resistance' },
        { value: '15+', label: 'Years Lifespan' },
        { value: '100%', label: 'Waterproof' }
      ],
      testimonial: {
        quote: 'Gas ke paas wala cabinet 5 saal se bilkul naya hai. Pehle har 2 saal mein repair karwana padta tha.',
        name: 'Mrs. Sharma',
        location: 'Lucknow'
      }
    },
    wardrobe: {
      title: 'Wardrobes & Furniture',
      icon: Shirt,
      tagline: 'Premium European-quality wardrobes, made in India',
      heroImage: '/crilam-wardrobe-hero.jpg',
      problems: [
        {
          problem: 'Edge banding peeling in humid bedrooms',
          solution: 'Moisture-proof permanent bond',
          icon: Droplets
        },
        {
          problem: 'Visible glue lines on premium finishes',
          solution: 'Ultra-thin invisible glue line (0.1mm)',
          icon: Sparkles
        },
        {
          problem: 'Laminate lifting in coastal areas',
          solution: 'Salt & humidity resistant for any climate',
          icon: Wind
        },
        {
          problem: 'Inconsistent quality across pieces',
          solution: 'Factory precision - every piece identical',
          icon: Shield
        }
      ],
      stats: [
        { value: '0.1mm', label: 'Glue Line' },
        { value: '6H', label: 'Surface Hardness' },
        { value: 'Zero', label: 'Bubbles' }
      ],
      testimonial: {
        quote: 'Coastal city mein rehte hain, pehle wardrobe 3 saal mein kharab ho jata tha. CriLam ka 6 saal se perfect hai.',
        name: 'Mr. Patel',
        location: 'Mumbai'
      }
    },
    doors: {
      title: 'Doors (CNC & Laminated)',
      icon: DoorOpen,
      tagline: 'Doors that perform in every room - bathroom to balcony',
      heroImage: '/crilam-doors-hero.jpg',
      problems: [
        {
          problem: 'Bathroom door laminate peeling',
          solution: '100% waterproof for wet areas',
          icon: Droplets
        },
        {
          problem: 'Main door facing sun exposure',
          solution: 'UV and heat stable - no fading',
          icon: Sun
        },
        {
          problem: 'AC room doors with temperature differential',
          solution: 'No thermal stress delamination',
          icon: Wind
        },
        {
          problem: 'Balcony doors facing weather',
          solution: 'Weather-resistant for outdoor exposure',
          icon: Shield
        }
      ],
      stats: [
        { value: '100%', label: 'Waterproof' },
        { value: 'UV', label: 'Stable' },
        { value: '10yr', label: 'Guarantee' }
      ],
      testimonial: {
        quote: 'Bathroom door 8 saal purana hai, aaj bhi naya jaisa. Pehle har saal change karna padta tha.',
        name: 'Mr. Gupta',
        location: 'Kanpur'
      }
    },
    panels: {
      title: 'Wall Panels',
      icon: PanelTop,
      tagline: 'Transform any wall into a design statement',
      heroImage: '/crilam-panels-hero.jpg',
      problems: [
        {
          problem: 'Panels near windows delaminating',
          solution: 'Weather-resistant bond handles sun & rain',
          icon: Sun
        },
        {
          problem: 'Commercial spaces with AC cycling',
          solution: 'Thermal stability - no expansion/contraction',
          icon: Wind
        },
        {
          problem: 'High-traffic areas showing wear',
          solution: '6H hardness resists scratches & impacts',
          icon: Shield
        },
        {
          problem: 'Cleaning chemicals damaging surface',
          solution: 'Chemical-resistant for easy maintenance',
          icon: Sparkles
        }
      ],
      stats: [
        { value: '6H', label: 'Hardness' },
        { value: 'Zero', label: 'Maintenance' },
        { value: '20+', label: 'Years Life' }
      ],
      testimonial: {
        quote: 'Office reception mein 4 saal se CriLam panels hain. Daily cleaning ke baad bhi finish perfect hai.',
        name: 'Verma Interiors',
        location: 'Delhi'
      }
    }
  };

  const currentApp = applications[activeApp];

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="application-benefits-page">
      {/* Hero Section */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/30 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div className="inline-block px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-sm font-bold mb-6">
              PUR TECHNOLOGY BENEFITS
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Outfit']">
              Problem → Solution
            </h1>
            <p className="text-xl text-[#D4AF37] font-medium mb-4">
              See How CriLam PUR-Bond™ Technology Solves Real Problems
            </p>
            <p className="text-lg text-white/70 max-w-2xl">
              Every application has unique challenges. See exactly how CriLam PUR-Bond™ technology 
              provides the perfect solution for each.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Application Selector */}
      <section className="py-6 bg-white border-b sticky top-24 z-30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-3 justify-center">
            {Object.entries(applications).map(([key, app]) => (
              <button
                key={key}
                onClick={() => setActiveApp(key)}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-all rounded-lg ${
                  activeApp === key
                    ? 'bg-[#004534] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`app-tab-${key}`}
              >
                <app.icon className="h-5 w-5" />
                {app.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <AnimatePresence mode="wait">
        <motion.section
          key={activeApp}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
          className="py-16 lg:py-24"
        >
          <div className="container mx-auto px-6 lg:px-12">
            {/* Application Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#004534] text-white rounded-full mb-6">
                <currentApp.icon className="h-6 w-6" />
                <span className="font-semibold">{currentApp.title}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#004534] mb-4 font-['Outfit']">
                {currentApp.tagline}
              </h2>
            </div>

            {/* Problem-Solution Table */}
            <div className="max-w-5xl mx-auto mb-16">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                {/* Table Header */}
                <div className="grid grid-cols-2 bg-gray-50 border-b">
                  <div className="p-4 lg:p-6 border-r border-gray-200">
                    <div className="flex items-center gap-2 text-red-600 font-bold">
                      <XCircle className="h-5 w-5" />
                      Problem (Regular Lamination)
                    </div>
                  </div>
                  <div className="p-4 lg:p-6">
                    <div className="flex items-center gap-2 text-green-600 font-bold">
                      <CheckCircle className="h-5 w-5" />
                      Solution (CriLam PUR)
                    </div>
                  </div>
                </div>

                {/* Table Rows */}
                {currentApp.problems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`grid grid-cols-2 ${index < currentApp.problems.length - 1 ? 'border-b border-gray-200' : ''}`}
                  >
                    <div className="p-4 lg:p-6 border-r border-gray-200 bg-red-50/50">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                          <item.icon className="h-5 w-5 text-red-500" />
                        </div>
                        <div>
                          <p className="text-gray-800 font-medium">{item.problem}</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 lg:p-6 bg-green-50/50">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-gray-800 font-medium">{item.solution}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Stats Bar */}
            <div className="max-w-4xl mx-auto mb-16">
              <div className="grid grid-cols-3 gap-4">
                {currentApp.stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="text-center p-6 bg-[#004534] text-white rounded-xl"
                  >
                    <div className="text-3xl lg:text-4xl font-bold text-[#D4AF37] mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm text-white/70">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Testimonial */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="max-w-3xl mx-auto"
            >
              <div className="bg-[#F7F8F5] border-l-4 border-[#D4AF37] p-6 lg:p-8 rounded-r-xl">
                <p className="text-lg lg:text-xl text-[#004534] italic mb-4">
                  &ldquo;{currentApp.testimonial.quote}&rdquo;
                </p>
                <div className="flex items-center gap-2 text-gray-600">
                  <span className="font-semibold">{currentApp.testimonial.name}</span>
                  <span>•</span>
                  <span>{currentApp.testimonial.location}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>
      </AnimatePresence>

      {/* Customer Value Summary */}
      <section className="py-20 lg:py-28 bg-[#004534] relative">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-center mb-16">
            <p className="label-mono text-[#D4AF37] mb-4">CUSTOMER VALUE</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']">
              What CriLam PUR Means For You
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Clock,
                title: '15+ Years',
                description: 'Products that last, not products you replace'
              },
              {
                icon: Shield,
                title: '10-Year Guarantee',
                description: 'Written warranty on all PUR-bonded products'
              },
              {
                icon: Award,
                title: 'Zero Maintenance',
                description: 'No repairs, no touch-ups, no headaches'
              },
              {
                icon: Sparkles,
                title: 'Premium Finish',
                description: 'European quality at Indian prices'
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 bg-white/[0.05] border border-white/[0.1] rounded-xl"
              >
                <div className="w-14 h-14 mx-auto mb-4 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <value.icon className="h-7 w-7 text-[#004534]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{value.title}</h3>
                <p className="text-white/70 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004534] mb-6 font-['Outfit']">
              Ready to Experience the Difference?
            </h2>
            <p className="text-lg text-gray-600 mb-10">
              See how CriLam PUR products can transform your space with our AI Visualizer, 
              or contact us for a personalized consultation.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/ai-visualizer">
                <button className="px-8 py-4 bg-[#004534] text-white font-medium hover:bg-[#D4AF37] hover:text-[#004534] transition-colors flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  Try AI Visualizer
                </button>
              </Link>
              <Link to="/contact">
                <button className="px-8 py-4 border-2 border-[#004534] text-[#004534] hover:bg-[#004534] hover:text-white transition-colors flex items-center gap-2">
                  Contact Us
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApplicationBenefits;
